import { initializeApp, FirebaseError } from "firebase/app";
import { 
  getAuth, 
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged, 
  User as FirebaseUser,
  AuthError
} from "firebase/auth";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getAnalytics, isSupported } from "firebase/analytics";
import { User } from "../types";

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase with error handling
let app;
let auth;
let db;
let analytics;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app, 'clara-ai'); // Use the clara-ai database
  
  // Initialize analytics only if supported
  isSupported().then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  }).catch(() => {
    console.warn('Analytics not supported in this environment');
  });
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  throw new Error('Firebase initialization failed. Please check your configuration.');
}

export { db };

const provider = new GoogleAuthProvider();
// Configure provider for better iOS compatibility
provider.setCustomParameters({
  prompt: 'select_account',
  // These parameters help with iOS but don't solve embedded browser issue
  display: 'popup'
});

// Add required scopes
provider.addScope('email');
provider.addScope('profile');

/**
 * Map Firebase User to User with safe defaults
 */
const mapUser = (user: FirebaseUser): User => {
  return {
    id: user.uid,
    name: user.displayName || 'Usuario',
    email: user.email || '',
    photoURL: user.photoURL || undefined
  };
};

/**
 * Handle Firebase Auth errors and return user-friendly messages
 */
const handleAuthError = (error: unknown): never => {
  if (error instanceof FirebaseError) {
    const authError = error as AuthError;
    switch (authError.code) {
      case 'auth/redirect-cancelled-by-user':
        throw new Error('Se canceló la autenticación.');
      case 'auth/network-request-failed':
        throw new Error('Error de conexión. Verificar la conexión a internet.');
      case 'auth/too-many-requests':
        throw new Error('Demasiados intentos fallidos. Esperar unos minutos e intentar nuevamente.');
      case 'auth/user-disabled':
        throw new Error('Esta cuenta ha sido deshabilitada.');
      case 'auth/operation-not-allowed':
        throw new Error('Este método de autenticación no está habilitado.');
      case 'auth/unauthorized-domain':
        throw new Error('Este dominio no está autorizado. Contactar al administrador.');
      default:
        console.error('Unhandled auth error:', authError.code, authError.message);
        // Check for embedded browser issue
        if (authError.message?.includes('disallowed_useragent') || 
            authError.message?.includes('403')) {
          throw new Error('EMBEDDED_BROWSER_ERROR');
        }
        throw new Error(`Error de autenticación: ${authError.message}`);
    }
  }
  // Check if the error message contains embedded browser indicators
  const errorMessage = String(error);
  if (errorMessage.includes('disallowed_useragent') || 
      (errorMessage.includes('403') && errorMessage.includes('Google'))) {
    throw new Error('EMBEDDED_BROWSER_ERROR');
  }
  throw error;
};

export const loginWithGoogle = async (): Promise<void> => {
  try {
    await signInWithRedirect(auth, provider);
    // User will be redirected to Google login
    // The result will be handled by handleRedirectResult
  } catch (error) {
    console.error("Error logging in with Google:", error);
    return handleAuthError(error);
  }
};

/**
 * Handle redirect result after Google login
 * This should be called on app initialization
 */
export const handleRedirectResult = async (): Promise<User | null> => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const userProfile = mapUser(result.user);
      // Save/update user profile in Firestore
      await saveUserProfile(userProfile);
      return userProfile;
    }
    return null;
  } catch (error) {
    console.error("Error handling redirect result:", error);
    throw error;
  }
};

/**
 * Save or update user profile in Firestore
 */
const saveUserProfile = async (user: User): Promise<void> => {
  try {
    const userRef = doc(db, 'users', user.id);
    await setDoc(userRef, {
      email: user.email,
      name: user.name,
      displayName: user.name,
      photoURL: user.photoURL || null,
      lastLogin: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving user profile:', error);
    // Don't throw - login should still succeed even if profile save fails
  }
};

export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out", error);
    // Don't throw on logout - just clear local state
  }
};

export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, async (user) => {
    try {
      if (user) {
        const userProfile = mapUser(user);
        // Save/update user profile on each auth state change
        await saveUserProfile(userProfile);
        callback(userProfile);
      } else {
        callback(null);
      }
    } catch (error) {
      console.error('Error in auth state change handler:', error);
      callback(null);
    }
  }, (error) => {
    console.error('Auth state observer error:', error);
    callback(null);
  });
};

/**
 * Get current user if authenticated
 */
export const getCurrentUser = (): User | null => {
  const user = auth.currentUser;
  return user ? mapUser(user) : null;
};
